import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { requestOrDelete } from "@/lib/approval";
import { PageHeader, Surface, Badge, Button, Modal, Input, Select, NewButton } from "@/components/ui-kit";
import TyreMap from "@/components/TyreMap";
import VehicleSearchSelect from "@/components/VehicleSearchSelect";
import { getAllPositions } from "@/lib/vehicleLayouts";
import { formatDate } from "@/lib/format";
import { ClockCounterClockwise, ArrowRight, ArrowsLeftRight, MagnifyingGlass, X } from "@phosphor-icons/react";
import { VEHICLE_CLASSES, STANDARDS, CONSTRUCTION_TYPES, deriveTyreSize } from "@/lib/tyreSpec";
import { downloadCsv, downloadMultiSectionCsv, printElement, printSections, regSort } from "@/lib/csv";
import ReportToolbar, { ZoomScope, ZOOM_DEFAULT } from "@/components/ReportToolbar";

const TYRE_STATUSES = ["in_stock", "fitted", "scrapped", "sold"];

const empty = {
    serial_number: "", brand: "", model: "", size: "",
    vehicle_class: "T", standard: "TRA",
    section_width_mm: "", aspect_ratio: "", overall_diameter_mm: "",
    construction_type: "R", rim_diameter_in: "",
    purchase_cost: 0, status: "in_stock", tread_depth: 8, retread_count: 0,
};

export default function Tyres() {
    const { user } = useAuth();
    const [list, setList] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);
    const [editing, setEditing] = useState(null);
    const [fitOpen, setFitOpen] = useState(null);
    const [fitForm, setFitForm] = useState({ vehicle_id: "", position: "", odometer_at_fit: 0 });
    const [fitVehicleTyres, setFitVehicleTyres] = useState([]);
    const [historyTyre, setHistoryTyre] = useState(null);
    const [history, setHistory] = useState([]);
    const [scrapInfo, setScrapInfo] = useState(null); // { scrapped, scrap_reason, files: [...] }
    const [switchFor, setSwitchFor] = useState(null);
    const [switchForm, setSwitchForm] = useState({
        vehicle_id: "", position: "",
        odometer_old_vehicle_at_remove: 0, odometer_at_fit: 0, notes: "",
    });
    const [switchVehicleTyres, setSwitchVehicleTyres] = useState([]);

    // Filters
    const [search, setSearch] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [filterBrand, setFilterBrand] = useState("");
    const [filterSize, setFilterSize] = useState("");
    const [filterFittedVehicle, setFilterFittedVehicle] = useState("");
    const [filterRetread, setFilterRetread] = useState(""); // 0 | 1+ | 2+
    const tableRef = useRef(null);

    // Server-side pagination state — latest tyres surface first
    const PAGE_SIZE = 20;
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [busy, setBusy] = useState(false);
    const [zoom, setZoom] = useState(ZOOM_DEFAULT);

    const load = useCallback(async (targetPage) => {
        const tp = targetPage ?? 1;
        setBusy(true);
        try {
            const params = new URLSearchParams();
            if (search.trim()) params.set("q", search.trim());
            if (filterStatus) params.set("status", filterStatus);
            if (filterBrand) params.set("brand", filterBrand);
            if (filterSize) params.set("size", filterSize);
            if (filterFittedVehicle) params.set("vehicle_id", filterFittedVehicle);
            if (filterRetread === "1+") params.set("retread_min", "1");
            else if (filterRetread === "2+") params.set("retread_min", "2");
            params.set("page", String(tp));
            params.set("page_size", String(PAGE_SIZE));
            const r = await api.get(`/tyres?${params.toString()}`);
            const payload = r.data || {};
            let items = payload.items || [];
            // filter "0 retreads" client-side (backend has no exact-zero filter)
            if (filterRetread === "0") items = items.filter((t) => (t.retread_count || 0) === 0);
            setList(items);
            setTotal(payload.total ?? items.length);
            setPage(payload.page ?? tp);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to load tyres");
        } finally {
            setBusy(false);
        }
    }, [search, filterStatus, filterBrand, filterSize, filterFittedVehicle, filterRetread]);

    // Pagination state
    useEffect(() => {
        load(1);
        api.get("/vehicles").then((r) => setVehicles(r.data));
    }, [load]);

    const save = async () => {
        try {
            const derived = deriveTyreSize(form);
            const payload = {
                ...form,
                size: form.size || derived || "",
                section_width_mm: form.section_width_mm ? Number(form.section_width_mm) : null,
                aspect_ratio: form.aspect_ratio ? Number(form.aspect_ratio) : null,
                overall_diameter_mm: form.overall_diameter_mm ? Number(form.overall_diameter_mm) : null,
                rim_diameter_in: form.rim_diameter_in ? Number(form.rim_diameter_in) : null,
                purchase_cost: Number(form.purchase_cost) || 0,
                tread_depth: Number(form.tread_depth) || 0,
                retread_count: Number(form.retread_count) || 0,
            };
            if (!payload.size) {
                toast.error("Fill size fields (Section width, Construction, Rim) — size will auto-compose.");
                return;
            }
            if (editing) await api.put(`/tyres/${editing}`, payload);
            else await api.post("/tyres", payload);
            toast.success("Saved");
            setOpen(false); setEditing(null); setForm(empty); load(page);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail, e)); }
    };

    const edit = (t) => { setForm({ ...empty, ...t }); setEditing(t.id); setOpen(true); };
    const del = async (t) => {
        const r = await requestOrDelete("tyre", t.id, `tyre ${t.serial_number}`, user?.role);
        if (r.ok && !r.requested) load(page);
    };

    // When vehicle is picked in Fit modal, fetch its currently fitted tyres for visual context
    useEffect(() => {
        if (!fitForm.vehicle_id) { setFitVehicleTyres([]); return; }
        api.get(`/tyres/by-vehicle/${fitForm.vehicle_id}`).then((r) => setFitVehicleTyres(r.data));
    }, [fitForm.vehicle_id]);

    const openFit = (t) => {
        setFitOpen(t);
        setFitForm({ vehicle_id: "", position: "", odometer_at_fit: 0 });
        setFitVehicleTyres([]);
    };

    const fit = async () => {
        if (!fitForm.vehicle_id || !fitForm.position) {
            toast.error("Select a vehicle and a position");
            return;
        }
        try {
            await api.post(`/tyres/${fitOpen.id}/fit`, { ...fitForm, odometer_at_fit: Number(fitForm.odometer_at_fit) });
            toast.success("Tyre fitted");
            setFitOpen(null); load(page);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const remove = async (t) => {
        const odo = window.prompt("Current odometer reading?", "0");
        if (odo === null) return;
        try {
            await api.post(`/tyres/${t.id}/remove`, { odometer: Number(odo) || 0 });
            toast.success("Removed"); load(page);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const retread = async (t) => {
        const cost = window.prompt("Retread cost (₹)?", "0");
        if (cost === null) return;
        await api.post(`/tyres/${t.id}/retread`, { cost: Number(cost) || 0 }); toast.success("Retreaded"); load(page);
    };

    // Scrap tyre — modal with mandatory reason + photos
    const [scrapFor, setScrapFor] = useState(null);
    const [scrapForm, setScrapForm] = useState({ reason: "", notes: "" });
    const [scrapFiles, setScrapFiles] = useState([]);
    const [scrapSubmitting, setScrapSubmitting] = useState(false);

    // ---------- Sell tyre ----------
    const emptySale = { buyer_name: "", sale_date: new Date().toISOString().slice(0, 10), sale_amount_inr: "", buyer_phone: "", buyer_address: "", notes: "" };
    const [sellFor, setSellFor] = useState(null);
    const [sellForm, setSellForm] = useState(emptySale);
    const [sellSubmitting, setSellSubmitting] = useState(false);

    const openSell = (t) => { setSellFor(t); setSellForm(emptySale); };
    const submitSell = async () => {
        if (!sellFor) return;
        if (!sellForm.buyer_name.trim()) { toast.error("Buyer name is required"); return; }
        const amt = Number(sellForm.sale_amount_inr);
        if (!Number.isFinite(amt) || amt <= 0) { toast.error("Enter a valid sale amount"); return; }
        setSellSubmitting(true);
        const isAdmin = user?.role === "admin";
        const saleInfo = {
            buyer_name: sellForm.buyer_name.trim(),
            sale_date: sellForm.sale_date || null,
            sale_amount_inr: amt,
            buyer_phone: sellForm.buyer_phone.trim() || null,
            buyer_address: sellForm.buyer_address.trim() || null,
            notes: sellForm.notes.trim() || null,
        };
        try {
            if (isAdmin) {
                await api.post(`/tyres/${sellFor.id}/sell`, saleInfo);
                toast.success(`Tyre sold to ${sellForm.buyer_name.trim()}`);
            } else {
                await api.post(`/approvals`, {
                    entity_type: "tyre",
                    entity_id: sellFor.id,
                    action: "sell",
                    reason: sellForm.notes.trim() || null,
                    sale_info: saleInfo,
                });
                toast.success("Sale request submitted for admin approval.");
            }
            setSellFor(null);
            load(page);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail, e) || "Sale failed");
        } finally {
            setSellSubmitting(false);
        }
    };

    const undoSale = async (t) => {
        if (!window.confirm(`Undo sale of tyre ${t.serial_number}? It will return to 'in_stock'.`)) return;
        try {
            await api.post(`/tyres/${t.id}/undo-sale`);
            toast.success("Sale reversed");
            load(page);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail, e) || "Undo failed");
        }
    };

    const openScrap = (t) => {
        setScrapFor(t);
        setScrapForm({ reason: "", notes: "" });
        setScrapFiles([]);
    };

    const submitScrap = async () => {
        if (!scrapFor) return;
        if (!scrapForm.reason.trim()) { toast.error("Reason for scrapping is required"); return; }
        if (scrapFiles.length === 0) { toast.error("At least one photo of the scrapped tyre is required"); return; }
        setScrapSubmitting(true);
        const tid = toast.loading("Scrapping tyre & uploading photos…");
        try {
            await api.post(`/tyres/${scrapFor.id}/scrap`, {
                reason: scrapForm.reason.trim(),
                notes: scrapForm.notes.trim() || null,
            });
            // Upload photos against the tyre entity, labelled "scrap_photo"
            let ok = 0;
            for (const f of scrapFiles) {
                const fd = new FormData(); fd.append("file", f);
                try {
                    await api.post(
                        `/upload?entity_type=tyre&entity_id=${scrapFor.id}&label=scrap_photo`,
                        fd, { headers: { "Content-Type": "multipart/form-data" } },
                    );
                    ok += 1;
                } catch (err) { console.warn("Scrap photo upload failed for one file:", err); }
            }
            toast.success(`Tyre scrapped · ${ok} photo(s) uploaded`, { id: tid });
            setScrapFor(null); setScrapForm({ reason: "", notes: "" }); setScrapFiles([]);
            load(page);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Scrap failed", { id: tid });
        } finally {
            setScrapSubmitting(false);
        }
    };

    const showHistory = async (t) => {
        const r = await api.get(`/tyres/${t.id}/history`);
        setHistory(r.data); setHistoryTyre(t);
        // Best-effort fetch the scrap info if the tyre was scrapped
        if (t.status === "scrapped") {
            try {
                const s = await api.get(`/tyres/${t.id}/scrap-info`);
                setScrapInfo(s.data);
            } catch { setScrapInfo(null); }
        } else {
            setScrapInfo(null);
        }
    };

    const openSwitch = (t) => {
        setSwitchFor(t);
        setSwitchForm({
            vehicle_id: "", position: "",
            odometer_old_vehicle_at_remove: vehMap[t.current_vehicle_id]?.odometer || 0,
            odometer_at_fit: 0, notes: "",
        });
        setSwitchVehicleTyres([]);
    };

    const submitSwitch = async () => {
        if (!switchForm.vehicle_id || !switchForm.position) {
            toast.error("Pick a new vehicle and position");
            return;
        }
        try {
            await api.post(`/tyres/${switchFor.id}/switch`, {
                vehicle_id: switchForm.vehicle_id,
                position: switchForm.position,
                odometer_old_vehicle_at_remove: Number(switchForm.odometer_old_vehicle_at_remove) || 0,
                odometer_at_fit: Number(switchForm.odometer_at_fit) || 0,
                notes: switchForm.notes,
            });
            toast.success("Tyre switched to new vehicle");
            setSwitchFor(null); load(page);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const vehMap = Object.fromEntries(vehicles.map((v) => [v.id, v]));
    const fitVehicle = vehicles.find((v) => v.id === fitForm.vehicle_id);
    const validPositions = fitVehicle ? getAllPositions(fitVehicle.vehicle_type_code || "truck_6") : [];
    const switchVehicle = vehicles.find((v) => v.id === switchForm.vehicle_id);
    const switchValidPositions = switchVehicle
        ? getAllPositions(switchVehicle.vehicle_type_code || "truck_6")
        : [];

    // Load tyres on the target switch vehicle so the map shows occupied positions
    useEffect(() => {
        if (!switchForm.vehicle_id) { setSwitchVehicleTyres([]); return; }
        api.get(`/tyres/by-vehicle/${switchForm.vehicle_id}`)
            .then((r) => setSwitchVehicleTyres(r.data))
            .catch(() => setSwitchVehicleTyres([]));
    }, [switchForm.vehicle_id]);

    const brands = useMemo(
        () => Array.from(new Set(list.map((t) => t.brand).filter(Boolean))).sort(),
        [list]
    );
    const sizes = useMemo(
        () => Array.from(new Set(list.map((t) => t.size).filter(Boolean))).sort(),
        [list]
    );
    // Memoize alphanumeric vehicle list for any dropdown that needs it.
    const sortedVehicles = useMemo(() => [...vehicles].sort(regSort), [vehicles]);

    // Server-side filtering is now in place. The `list` is already the page's
    // filtered slice; rename via const for downstream readability.
    const filtered = list;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    // Refetch when filters change
    useEffect(() => {
        setPage(1);
        load(1);
    }, [search, filterStatus, filterBrand, filterSize, filterFittedVehicle, filterRetread, load]);

    const clearFilters = () => {
        setSearch(""); setFilterStatus(""); setFilterBrand("");
        setFilterSize(""); setFilterFittedVehicle(""); setFilterRetread("");
    };
    const hasFilters = search || filterStatus || filterBrand || filterSize || filterFittedVehicle || filterRetread;

    // Cost-per-km = (purchase + Σretread costs) / live_km. Returns null when km = 0.
    const costPerKm = (t) => {
        const km = t.live_km ?? t.total_km ?? 0;
        if (km <= 0) return null;
        const total = (t.purchase_cost || 0) + (t.total_retread_cost || 0);
        return total / km;
    };
    const fmtCpk = (v) => v == null ? "—" : `₹${v.toFixed(2)}`;

    // Helper: fetch ALL tyres matching current filters (no pagination) for exports
    const fetchAllFiltered = useCallback(async () => {
        const params = new URLSearchParams();
        if (search.trim()) params.set("q", search.trim());
        if (filterStatus) params.set("status", filterStatus);
        if (filterBrand) params.set("brand", filterBrand);
        if (filterSize) params.set("size", filterSize);
        if (filterFittedVehicle) params.set("vehicle_id", filterFittedVehicle);
        if (filterRetread === "1+") params.set("retread_min", "1");
        else if (filterRetread === "2+") params.set("retread_min", "2");
        const r = await api.get(`/tyres${params.toString() ? `?${params.toString()}` : ""}`);
        let rows = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        if (filterRetread === "0") rows = rows.filter((t) => (t.retread_count || 0) === 0);
        return rows;
    }, [search, filterStatus, filterBrand, filterSize, filterFittedVehicle, filterRetread]);

    // Fetch fitment history for an arbitrary list of tyres
    const fetchHistoryFor = async (rows) => {
        const out = [];
        for (const t of rows) {
            try {
                const r = await api.get(`/tyres/${t.id}/history`);
                for (const h of r.data) out.push({ serial: t.serial_number, brand: t.brand, ...h });
            } catch (err) { console.warn("Tyre history fetch failed:", err); }
        }
        return out;
    };

    const exportCsv = async () => {
        const tid = toast.loading("Compiling export…");
        const allRows = await fetchAllFiltered();
        const history = await fetchHistoryFor(allRows);
        downloadMultiSectionCsv(`tyres-${new Date().toISOString().slice(0, 10)}.csv`, [
            {
                title: `Tyres (${allRows.length} record(s))`,
                rows: allRows,
                columns: [
                    ["SERIAL", "serial_number"], ["BRAND", "brand"], ["MODEL", "model"],
                    ["SIZE", "size"],
                    ["RETREAD COUNT", "retread_count"],
                    ["TOTAL KM", (t) => t.live_km ?? t.total_km ?? 0],
                    ["PURCHASE COST (INR)", "purchase_cost"],
                    ["RETREAD COST TOTAL (INR)", (t) => (t.total_retread_cost || 0).toFixed(2)],
                    ["COST / KM (INR)", (t) => { const v = costPerKm(t); return v == null ? "" : v.toFixed(4); }],
                    ["STATUS", "status"],
                    ["FITTED ON VEHICLE", (t) => vehMap[t.current_vehicle_id]?.reg_number || ""],
                    ["POSITION", "current_position"],
                    ["PURCHASE DATE", (t) => formatDate(t.purchase_date)],
                ],
            },
            {
                title: `Fitment history (${history.length} entries)`,
                rows: history,
                columns: [
                    ["SERIAL", "serial"], ["BRAND", "brand"],
                    ["VEHICLE", "vehicle_reg"],
                    ["POSITION", "position"],
                    ["FITTED AT", (h) => formatDate(h.fitted_at)],
                    ["REMOVED AT", (h) => h.removed_at ? formatDate(h.removed_at) : "—"],
                    ["ODO @ FIT", "odometer_at_fit"],
                    ["ODO @ REMOVE", (h) => h.odometer_at_remove ?? "—"],
                    ["KM RUN", "km_run"],
                    ["REMOVAL REASON", "removal_reason"],
                ],
            },
        ]);
        toast.success(`Exported · ${allRows.length} tyres + ${history.length} history rows`, { id: tid });
    };

    const exportPdf = async () => {
        const tid = toast.loading("Compiling export…");
        const allRows = await fetchAllFiltered();
        const history = await fetchHistoryFor(allRows);
        printSections(`EAZYFLEET · Tyres · ${allRows.length} record(s)`, [
            {
                title: `Tyres`,
                rows: allRows,
                columns: [
                    { label: "SERIAL", key: "serial_number" },
                    { label: "BRAND", key: "brand" },
                    { label: "SIZE", key: "size" },
                    { label: "STATUS", key: "status" },
                    { label: "FITTED ON", key: (t) => vehMap[t.current_vehicle_id]?.reg_number || "" },
                    { label: "TOTAL KM", key: (t) => (t.live_km ?? t.total_km ?? 0).toLocaleString() },
                    { label: "RETREADS", key: "retread_count" },
                    { label: "₹/KM", key: (t) => { const v = costPerKm(t); return v == null ? "—" : `₹${v.toFixed(2)}`; } },
                ],
            },
            {
                title: `Fitment history (${history.length})`,
                rows: history,
                columns: [
                    { label: "SERIAL", key: "serial" },
                    { label: "VEHICLE", key: "vehicle_reg" },
                    { label: "POSITION", key: "position" },
                    { label: "FITTED AT", key: (h) => formatDate(h.fitted_at) },
                    { label: "REMOVED AT", key: (h) => h.removed_at ? formatDate(h.removed_at) : "—" },
                    { label: "KM RUN", key: "km_run" },
                    { label: "REASON", key: "removal_reason" },
                ],
            },
        ]);
        toast.success("Print dialog opened", { id: tid });
    };

    return (
        <div className="p-6 lg:p-8 animate-fade-in">
            <PageHeader
                eyebrow="ASSETS"
                title="Tyres"
                subtitle={`${list.length} tyres tracked · serial-based lifecycle`}
                action={<NewButton onClick={() => { setForm(empty); setEditing(null); setOpen(true); }} label="Add tyre" testid="add-tyre-btn" />}
            />

            {/* Search & filter bar */}
            <Surface className="px-4 py-3 mb-4 flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[220px] max-w-sm">
                    <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-fleet-inkMuted" />
                    <input
                        type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by serial, brand or model…"
                        className="w-full pl-9 pr-3 py-2 text-sm border border-fleet-border rounded-md focus:outline-none focus:border-fleet-ink"
                        data-testid="tyre-search-input"
                    />
                </div>
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-3 py-2 text-sm border border-fleet-border rounded-md bg-white focus:outline-none focus:border-fleet-ink"
                    data-testid="tyre-filter-status">
                    <option value="">All statuses</option>
                    {TYRE_STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                </select>
                <select value={filterBrand} onChange={(e) => setFilterBrand(e.target.value)}
                    className="px-3 py-2 text-sm border border-fleet-border rounded-md bg-white focus:outline-none focus:border-fleet-ink"
                    data-testid="tyre-filter-brand">
                    <option value="">All brands</option>
                    {brands.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
                <select value={filterSize} onChange={(e) => setFilterSize(e.target.value)}
                    className="px-3 py-2 text-sm border border-fleet-border rounded-md bg-white focus:outline-none focus:border-fleet-ink"
                    data-testid="tyre-filter-size">
                    <option value="">All sizes</option>
                    {sizes.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select value={filterFittedVehicle} onChange={(e) => setFilterFittedVehicle(e.target.value)}
                    className="px-3 py-2 text-sm border border-fleet-border rounded-md bg-white focus:outline-none focus:border-fleet-ink"
                    data-testid="tyre-filter-vehicle">
                    <option value="">All vehicles</option>
                    {sortedVehicles.map((v) => <option key={v.id} value={v.id}>{v.reg_number}</option>)}
                </select>
                <select value={filterRetread} onChange={(e) => setFilterRetread(e.target.value)}
                    className="px-3 py-2 text-sm border border-fleet-border rounded-md bg-white focus:outline-none focus:border-fleet-ink"
                    data-testid="tyre-filter-retread">
                    <option value="">Any retreads</option>
                    <option value="0">0 retreads (virgin)</option>
                    <option value="1+">1+ retreads</option>
                    <option value="2+">2+ retreads</option>
                </select>
                {hasFilters && (
                    <button onClick={clearFilters} className="text-xs text-fleet-inkSoft hover:text-fleet-ink underline flex items-center gap-1" data-testid="tyre-clear-filters">
                        <X size={12} /> Clear
                    </button>
                )}
            </Surface>

            <Surface className="px-4 py-2 mb-4">
                <ReportToolbar
                    page={page} totalPages={totalPages} total={total} pageSize={PAGE_SIZE}
                    onPageChange={(p) => { setPage(p); load(p); }}
                    onRefresh={() => load(page)}
                    zoom={zoom} onZoomChange={setZoom}
                    onExportCsv={exportCsv}
                    onExportPdf={exportPdf}
                    busy={busy}
                />
            </Surface>

            <Surface className="overflow-hidden">
                <ZoomScope zoom={zoom}>
                <table className="w-full text-sm" ref={tableRef}>
                    <thead className="bg-slate-50"><tr className="text-left">
                        {["SERIAL", "BRAND", "SIZE", "STATUS", "FITTED ON", "TOTAL KM", "RETREADS", "₹/KM", ""].map((h, i, arr) => <th key={h} className={`label-mono px-5 py-3 font-normal${i === arr.length - 1 ? " no-print" : ""}`}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                        {filtered.map((t) => (
                            <tr key={t.id} className="border-t border-fleet-border data-grid-row" data-testid={`tyre-row-${t.id}`}>
                                <td className="px-5 py-3 font-mono">{t.serial_number}</td>
                                <td className="px-5 py-3">{t.brand}</td>
                                <td className="px-5 py-3 font-mono">{t.size}</td>
                                <td className="px-5 py-3">
                                    <Badge variant={t.status}>{t.status === "in_stock" ? "in stock" : t.status}</Badge>
                                    {t.status === "in_stock" && (t.retread_count || 0) > 0 && (
                                        <Badge variant="retreaded" className="ml-1">retreaded</Badge>
                                    )}
                                </td>
                                <td className="px-5 py-3 font-mono text-xs">{t.current_vehicle_id ? `${vehMap[t.current_vehicle_id]?.reg_number || "?"} / ${t.current_position}` : "—"}</td>
                                <td className="px-5 py-3 font-mono" title={
                                    t.status === "fitted" && (t.current_km_run || 0) > 0
                                        ? `Includes ${(t.current_km_run || 0).toLocaleString()} km on current vehicle (live)`
                                        : ""
                                }>
                                    {(t.live_km ?? t.total_km ?? 0).toLocaleString()}
                                    {t.status === "fitted" && (t.current_km_run || 0) > 0 && (
                                        <span className="ml-1 text-[10px] text-fleet-inkSoft">(live)</span>
                                    )}
                                </td>
                                <td className="px-5 py-3 font-mono">{t.retread_count || 0}</td>
                                <td className="px-5 py-3 font-mono"
                                    title={`Purchase ₹${(t.purchase_cost||0).toLocaleString()} + Retreads ₹${(t.total_retread_cost||0).toLocaleString()} ÷ ${(t.live_km ?? t.total_km ?? 0).toLocaleString()} km`}>
                                    {fmtCpk(costPerKm(t))}
                                </td>
                                <td className="px-5 py-3 text-right whitespace-nowrap no-print">
                                    <Button variant="ghost" size="sm" onClick={() => showHistory(t)} data-testid={`history-${t.id}`}><ClockCounterClockwise size={14} /></Button>
                                    {t.status === "in_stock" && (
                                        <Button variant="ghost" size="sm" onClick={() => openFit(t)} data-testid={`fit-${t.id}`}>Fit</Button>
                                    )}
                                    {t.status === "fitted" && (
                                        <>
                                            <Button variant="ghost" size="sm" onClick={() => openSwitch(t)} data-testid={`switch-${t.id}`}><ArrowsLeftRight size={14} /> Switch</Button>
                                            <Button variant="ghost" size="sm" onClick={() => remove(t)} data-testid={`remove-${t.id}`}>Remove</Button>
                                        </>
                                    )}
                                    {t.status === "in_stock" && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => retread(t)}
                                            disabled={!t.fitted_since_last_retread || (t.live_km ?? t.total_km ?? 0) <= 0}
                                            title={
                                                (t.live_km ?? t.total_km ?? 0) <= 0
                                                    ? "New tyre (odometer 0) — must be fitted and run on a vehicle before retreading"
                                                    : !t.fitted_since_last_retread
                                                        ? "Already retreaded since last fitment — fit it on a vehicle again before retreading"
                                                        : "Send tyre to retread"
                                            }
                                            data-testid={`retread-${t.id}`}
                                        >
                                            Retread
                                        </Button>
                                    )}
                                    {t.status !== "scrapped" && t.status !== "sold" && <Button variant="ghost" size="sm" onClick={() => openScrap(t)} data-testid={`scrap-${t.id}`}>Scrap</Button>}
                                    {t.status !== "sold" && t.status !== "scrapped" && <Button variant="ghost" size="sm" onClick={() => openSell(t)} data-testid={`sell-${t.id}`}>{user?.role === "admin" ? "Sell" : "Request sale"}</Button>}
                                    {t.status === "sold" && <Button variant="ghost" size="sm" onClick={() => undoSale(t)} data-testid={`undo-sale-${t.id}`}>Undo sale</Button>}
                                    {t.status !== "sold" && <Button variant="ghost" size="sm" onClick={() => edit(t)} data-testid={`edit-tyre-${t.id}`}>Edit</Button>}
                                    {t.status !== "sold" && <Button variant="ghost" size="sm" onClick={() => del(t)} data-testid={`del-tyre-${t.id}`}>Del</Button>}
                                </td>
                            </tr>
                        ))}
                        {filtered.length === 0 && <tr><td colSpan={9} className="px-5 py-10 text-center text-sm text-fleet-inkSoft">{total === 0 ? "No tyres yet." : "No tyres match the current filters."}</td></tr>}
                    </tbody>
                </table>
                </ZoomScope>
            </Surface>

            <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit tyre" : "Add tyre"} size="lg"
                footer={<><Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="save-tyre">Save</Button></>}>
                <div className="grid grid-cols-2 gap-4">
                    <Input label="Serial #" value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value.toUpperCase() })} data-testid="tyre-serial-input" />
                    <Input label="Brand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="MRF, JK, Apollo…" />
                    <Input label="Model" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} />
                </div>

                <div className="mt-5 mb-2 label-mono">SIZE & SPECIFICATION (per metric tyre template)</div>
                <div className="grid grid-cols-2 gap-4">
                    <Select label="Vehicle class" value={form.vehicle_class}
                        onChange={(e) => setForm({ ...form, vehicle_class: e.target.value })}
                        options={VEHICLE_CLASSES} data-testid="tyre-vehicle-class" />
                    <Select label="Standard" value={form.standard}
                        onChange={(e) => setForm({ ...form, standard: e.target.value })}
                        options={STANDARDS} />
                    <Input label="Section width (mm)" type="number" min="100" value={form.section_width_mm}
                        onChange={(e) => setForm({ ...form, section_width_mm: e.target.value })} placeholder="e.g. 295" data-testid="tyre-section-width" />
                    <Input label="Aspect ratio (%)" type="number" min="20" value={form.aspect_ratio}
                        onChange={(e) => setForm({ ...form, aspect_ratio: e.target.value })} placeholder="e.g. 80" />
                    <Input label="Overall diameter (mm)" type="number" min="300" value={form.overall_diameter_mm}
                        onChange={(e) => setForm({ ...form, overall_diameter_mm: e.target.value })} placeholder="e.g. 1050" />
                    <Select label="Construction type" value={form.construction_type}
                        onChange={(e) => setForm({ ...form, construction_type: e.target.value })}
                        options={CONSTRUCTION_TYPES} />
                    <Input label="Rim diameter (inch)" type="number" step="0.1" min="8" value={form.rim_diameter_in}
                        onChange={(e) => setForm({ ...form, rim_diameter_in: e.target.value })} placeholder="e.g. 22.5" data-testid="tyre-rim-diameter" />
                    <Input label="Size (auto)" value={form.size || (deriveTyreSize(form) || "")}
                        onChange={(e) => setForm({ ...form, size: e.target.value })}
                        placeholder="e.g. 295/80R22.5" data-testid="tyre-size-input" />
                </div>

                <div className="mt-5 grid grid-cols-2 gap-4">
                    <Input label="Purchase cost (₹)" type="number" value={form.purchase_cost} onChange={(e) => setForm({ ...form, purchase_cost: e.target.value })} />
                </div>
            </Modal>

            {/* Fit Modal — with live tyre map */}
            <Modal open={!!fitOpen} onClose={() => setFitOpen(null)} size="xl"
                title={`Fit tyre ${fitOpen?.serial_number || ""} → vehicle position`}
                footer={<><Button variant="secondary" onClick={() => setFitOpen(null)}>Cancel</Button><Button onClick={fit} data-testid="confirm-fit" disabled={!fitForm.vehicle_id || !fitForm.position}>Fit at {fitForm.position || "—"}</Button></>}>
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                    {/* Form column */}
                    <div className="lg:col-span-2 space-y-4">
                        <VehicleSearchSelect
                            vehicles={vehicles}
                            value={fitForm.vehicle_id}
                            onChange={(id) => setFitForm({ ...fitForm, vehicle_id: id, position: "" })}
                            label="Vehicle"
                            placeholder="Search by reg #…"
                            testid="fit-vehicle-search"
                        />
                        <Select label="Position" value={fitForm.position}
                            onChange={(e) => setFitForm({ ...fitForm, position: e.target.value })}
                            options={[{ value: "", label: "— pick on map or here —" }, ...validPositions.map((p) => ({ value: p, label: p }))]} />
                        <Input label="Odometer at fit (km)" type="number" value={fitForm.odometer_at_fit}
                            onChange={(e) => setFitForm({ ...fitForm, odometer_at_fit: e.target.value })} />
                        {fitVehicle && (
                            <div className="text-xs text-fleet-inkSoft border border-fleet-border rounded-md p-3 bg-slate-50">
                                <div className="label-mono mb-1">SELECTED</div>
                                <div className="font-mono">{fitVehicle.reg_number}</div>
                                <div className="text-[11px] mt-0.5">{fitVehicle.vehicle_type_code || "default layout"}</div>
                            </div>
                        )}
                    </div>
                    {/* Map column */}
                    <div className="lg:col-span-3 border border-fleet-border rounded-md p-4 bg-white">
                        {fitVehicle ? (
                            <TyreMap
                                layoutCode={fitVehicle.vehicle_type_code || "truck_6"}
                                tyres={fitVehicleTyres}
                                selectedPosition={fitForm.position}
                                onPositionClick={(code) => setFitForm({ ...fitForm, position: code })}
                            />
                        ) : (
                            <div className="text-center py-12 text-sm text-fleet-inkSoft">Pick a vehicle to see its tyre layout.</div>
                        )}
                    </div>
                </div>
            </Modal>

            {/* History modal */}
            <Modal open={!!historyTyre} onClose={() => { setHistoryTyre(null); setScrapInfo(null); }} size="lg"
                title={`Tyre history · ${historyTyre?.serial_number || ""}`}>
                {history.length === 0 && !scrapInfo?.scrapped ? (
                    <div className="text-center py-10 text-sm text-fleet-inkSoft">No history yet — tyre has not been fitted to any vehicle.</div>
                ) : (
                    <>
                        {history.length > 0 && (
                            <>
                                <div className="flex items-center gap-2 flex-wrap mb-5">
                                    {history.map((h, i) => (
                                        <span key={h.id} className="inline-flex items-center gap-2">
                                            <span className="px-2 py-1 bg-slate-100 border border-fleet-border rounded font-mono text-xs">{h.vehicle_reg} / {h.position}</span>
                                            {i < history.length - 1 && <ArrowRight size={12} className="text-fleet-inkSoft" />}
                                        </span>
                                    ))}
                                    {scrapInfo?.scrapped && (
                                        <>
                                            <ArrowRight size={12} className="text-fleet-inkSoft" />
                                            <span className="px-2 py-1 bg-red-50 border border-red-200 text-red-800 rounded font-mono text-xs">SCRAPPED</span>
                                        </>
                                    )}
                                </div>
                                <table className="w-full text-sm border border-fleet-border rounded-md overflow-hidden">
                                    <thead className="bg-slate-50"><tr className="text-left">
                                        {["VEHICLE / POSITION", "FITTED", "REMOVED", "ODO @ FIT", "ODO @ REMOVE", "KM RUN"].map((h) => <th key={h} className="label-mono px-4 py-2 font-normal">{h}</th>)}
                                    </tr></thead>
                                    <tbody>
                                        {history.map((h) => (
                                            <tr key={h.id} className="border-t border-fleet-border">
                                                <td className="px-4 py-2 font-mono">{h.vehicle_reg} / {h.position}</td>
                                                <td className="px-4 py-2 font-mono">{formatDate(h.fitted_at)}</td>
                                                <td className="px-4 py-2 font-mono">{h.removed_at ? formatDate(h.removed_at) : "—"}</td>
                                                <td className="px-4 py-2 font-mono">{(h.odometer_at_fit || 0).toLocaleString()}</td>
                                                <td className="px-4 py-2 font-mono">{h.odometer_at_remove != null ? h.odometer_at_remove.toLocaleString() : "—"}</td>
                                                <td className="px-4 py-2 font-mono">{(h.km_run || 0).toLocaleString()}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </>
                        )}

                        {scrapInfo?.scrapped && (
                            <div className="mt-6 border border-red-200 rounded-md overflow-hidden" data-testid="scrap-exit-panel">
                                <div className="bg-red-50 px-4 py-2 flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <span className="font-mono text-xs text-red-800 font-semibold uppercase tracking-wider">Scrap exit</span>
                                        <Badge variant="critical">SCRAPPED</Badge>
                                    </div>
                                    <span className="text-xs text-fleet-inkSoft font-mono">{scrapInfo.scrapped_at ? formatDate(scrapInfo.scrapped_at) : "—"}</span>
                                </div>
                                <div className="p-4 space-y-3 bg-white">
                                    <div>
                                        <div className="label-mono mb-1">Reason</div>
                                        <div className="text-sm font-medium">
                                            {(scrapInfo.scrap_reason || "—").replace(/_/g, " ")}
                                        </div>
                                    </div>
                                    {scrapInfo.scrap_notes && (
                                        <div>
                                            <div className="label-mono mb-1">Notes</div>
                                            <div className="text-sm text-fleet-inkSoft italic">"{scrapInfo.scrap_notes}"</div>
                                        </div>
                                    )}
                                    {scrapInfo.files && scrapInfo.files.length > 0 && (
                                        <div>
                                            <div className="label-mono mb-2">Photos ({scrapInfo.files.length})</div>
                                            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2.5">
                                                {scrapInfo.files.map((f) => {
                                                    const isImage = /\.(png|jpe?g|webp|gif|heic|heif)$/i.test(f.original_filename || "");
                                                    return (
                                                        <a
                                                            key={f.id}
                                                            href={fileUrl(f.id)}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="block aspect-square border border-fleet-border rounded-md overflow-hidden bg-slate-50 relative hover:border-fleet-ink transition-colors"
                                                            data-testid={`scrap-photo-${f.id}`}
                                                            title={f.original_filename}
                                                        >
                                                            {isImage ? (
                                                                <img src={fileUrl(f.id)} alt={f.original_filename} className="w-full h-full object-cover" />
                                                            ) : (
                                                                <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-fleet-inkSoft text-xs p-2 text-center">
                                                                    <span className="text-2xl">📄</span>
                                                                    <span className="truncate w-full">{f.original_filename}</span>
                                                                </div>
                                                            )}
                                                        </a>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </Modal>

            {/* Switch modal — move fitted tyre to a different vehicle/position */}
            <Modal open={!!switchFor} onClose={() => setSwitchFor(null)} size="xl"
                title={switchFor ? `Switch tyre ${switchFor.serial_number} to another vehicle` : ""}
                footer={<>
                    <Button variant="secondary" onClick={() => setSwitchFor(null)}>Cancel</Button>
                    <Button onClick={submitSwitch} data-testid="confirm-tyre-switch"
                        disabled={!switchForm.vehicle_id || !switchForm.position}>
                        Switch to {switchForm.position || "—"}
                    </Button>
                </>}>
                {switchFor && (
                    <>
                        <div className="px-3 py-2 mb-4 bg-slate-50 border border-fleet-border rounded-md text-xs">
                            Currently on <span className="font-mono font-semibold">{vehMap[switchFor.current_vehicle_id]?.reg_number || "—"}</span>
                            {" "}at position <span className="font-mono font-semibold">{switchFor.current_position}</span>.
                            Switching closes this fitment with the old-vehicle odometer and opens a new fitment with the new-vehicle odometer. km_run carries forward.
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                            <div className="lg:col-span-2 space-y-4">
                                <VehicleSearchSelect
                                    vehicles={vehicles}
                                    value={switchForm.vehicle_id}
                                    onChange={(id) => setSwitchForm({ ...switchForm, vehicle_id: id, position: "" })}
                                    label="New vehicle"
                                    placeholder="Search by reg #…"
                                    filter={(v) => v.id !== switchFor.current_vehicle_id}
                                    testid="switch-vehicle-search"
                                />
                                <Select label="New position" value={switchForm.position}
                                    onChange={(e) => setSwitchForm({ ...switchForm, position: e.target.value })}
                                    options={[{ value: "", label: "— pick on map or here —" },
                                        ...switchValidPositions.map((p) => ({ value: p, label: p }))]} />
                                <Input
                                    label={`Odometer of present vehicle (${vehMap[switchFor.current_vehicle_id]?.reg_number || "—"})`}
                                    type="number"
                                    value={switchForm.odometer_old_vehicle_at_remove}
                                    onChange={(e) => setSwitchForm({ ...switchForm, odometer_old_vehicle_at_remove: e.target.value })}
                                    data-testid="switch-old-odometer" />
                                <Input label="Odometer of new vehicle at fit" type="number"
                                    value={switchForm.odometer_at_fit}
                                    onChange={(e) => setSwitchForm({ ...switchForm, odometer_at_fit: e.target.value })}
                                    data-testid="switch-new-odometer" />
                                <Input label="Notes" value={switchForm.notes}
                                    onChange={(e) => setSwitchForm({ ...switchForm, notes: e.target.value })} />
                            </div>
                            <div className="lg:col-span-3 border border-fleet-border rounded-md p-4 bg-white">
                                {switchVehicle ? (
                                    <TyreMap
                                        layoutCode={switchVehicle.vehicle_type_code || "truck_6"}
                                        tyres={switchVehicleTyres}
                                        selectedPosition={switchForm.position}
                                        onPositionClick={(code) => setSwitchForm({ ...switchForm, position: code })}
                                    />
                                ) : (
                                    <div className="text-center py-12 text-sm text-fleet-inkSoft">Pick a vehicle to see its tyre layout.</div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </Modal>

            {/* Scrap tyre — mandatory reason + photo(s) */}
            <Modal
                open={!!scrapFor}
                onClose={() => !scrapSubmitting && setScrapFor(null)}
                title={scrapFor ? `Scrap tyre · ${scrapFor.serial_number}` : ""}
                size="lg"
                footer={<>
                    <Button variant="secondary" onClick={() => setScrapFor(null)} disabled={scrapSubmitting}>Cancel</Button>
                    <Button
                        onClick={submitScrap}
                        disabled={scrapSubmitting || !scrapForm.reason.trim() || scrapFiles.length === 0}
                        data-testid="confirm-scrap"
                    >
                        {scrapSubmitting ? "Scrapping…" : "Scrap tyre"}
                    </Button>
                </>}
            >
                {scrapFor && (
                    <div className="space-y-4">
                        <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-900">
                            Scrapping is permanent. The tyre will be marked <span className="font-mono">scrapped</span> and excluded from active stock.
                            Both <strong>reason</strong> and <strong>photo(s)</strong> are mandatory for the audit log.
                        </div>

                        <div>
                            <label className="label-mono block mb-1.5">Reason for scrapping <span className="text-fleet-critical">*</span></label>
                            <select
                                value={scrapForm.reason}
                                onChange={(e) => setScrapForm({ ...scrapForm, reason: e.target.value })}
                                className="w-full px-3 py-2 bg-white border border-fleet-border rounded-md text-sm focus:outline-none focus:border-fleet-ink"
                                data-testid="scrap-reason"
                            >
                                <option value="">Pick a reason…</option>
                                <option value="worn_out">Worn out (tread below limit)</option>
                                <option value="sidewall_damage">Sidewall damage / cut</option>
                                <option value="puncture_unrepairable">Puncture (unrepairable)</option>
                                <option value="blowout">Blowout</option>
                                <option value="impact_damage">Impact damage</option>
                                <option value="age_expired">Age expired</option>
                                <option value="manufacturing_defect">Manufacturing defect</option>
                                <option value="other">Other (specify in notes)</option>
                            </select>
                        </div>

                        <div>
                            <label className="label-mono block mb-1.5">Notes (optional)</label>
                            <textarea
                                value={scrapForm.notes}
                                onChange={(e) => setScrapForm({ ...scrapForm, notes: e.target.value })}
                                rows={2}
                                className="w-full px-3 py-2 bg-white border border-fleet-border rounded-md text-sm focus:outline-none focus:border-fleet-ink"
                                placeholder="Anything else worth noting on the audit log…"
                                data-testid="scrap-notes"
                            />
                        </div>

                        <div>
                            <label className="label-mono block mb-1.5">Photos of scrapped tyre <span className="text-fleet-critical">*</span></label>
                            <input
                                type="file"
                                accept="image/*,.pdf"
                                multiple
                                onChange={(e) => setScrapFiles(Array.from(e.target.files || []))}
                                className="w-full text-xs file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-fleet-border file:bg-white file:text-fleet-ink file:cursor-pointer"
                                data-testid="scrap-photos"
                            />
                            {scrapFiles.length === 0 ? (
                                <div className="text-xs text-fleet-inkSoft mt-1">At least one photo is required. You can select multiple.</div>
                            ) : (
                                <div className="text-xs text-fleet-inkSoft mt-1">
                                    {scrapFiles.length} file(s) selected · {scrapFiles.map((f) => f.name).join(", ")}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </Modal>

            <Modal
                open={!!sellFor}
                onClose={() => setSellFor(null)}
                title={sellFor ? `${user?.role === "admin" ? "Sell" : "Request sale of"} tyre · ${sellFor.serial_number}` : "Sell tyre"}
                size="md"
                footer={<>
                    <Button variant="secondary" onClick={() => setSellFor(null)} disabled={sellSubmitting}>Cancel</Button>
                    <Button onClick={submitSell} disabled={sellSubmitting || !sellForm.buyer_name.trim() || !sellForm.sale_amount_inr} data-testid="sell-tyre-submit">
                        {sellSubmitting ? "Submitting…" : (user?.role === "admin" ? "Record sale" : "Submit request")}
                    </Button>
                </>}
            >
                {sellFor && (
                    <div className="space-y-3">
                        {sellFor.current_vehicle_id && (
                            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                                This tyre is currently fitted on a vehicle. Selling will automatically remove it first.
                            </div>
                        )}
                        <Input label="Buyer name *" value={sellForm.buyer_name} onChange={(e) => setSellForm({ ...sellForm, buyer_name: e.target.value })} data-testid="sell-buyer-name" />
                        <div className="grid grid-cols-2 gap-3">
                            <Input label="Sale date" type="date" value={sellForm.sale_date} onChange={(e) => setSellForm({ ...sellForm, sale_date: e.target.value })} data-testid="sell-date" />
                            <Input label="Sale amount (₹) *" type="number" min="0" step="0.01" value={sellForm.sale_amount_inr} onChange={(e) => setSellForm({ ...sellForm, sale_amount_inr: e.target.value })} data-testid="sell-amount" />
                        </div>
                        <Input label="Buyer phone (optional)" value={sellForm.buyer_phone} onChange={(e) => setSellForm({ ...sellForm, buyer_phone: e.target.value })} />
                        <div>
                            <label className="label-mono text-xs text-fleet-inkSoft mb-1 block">Buyer address (optional)</label>
                            <textarea rows={2} value={sellForm.buyer_address} onChange={(e) => setSellForm({ ...sellForm, buyer_address: e.target.value })} className="w-full border border-fleet-border rounded-md px-3 py-2 text-sm" />
                        </div>
                        <div>
                            <label className="label-mono text-xs text-fleet-inkSoft mb-1 block">Notes / remarks (optional)</label>
                            <textarea rows={2} value={sellForm.notes} onChange={(e) => setSellForm({ ...sellForm, notes: e.target.value })} className="w-full border border-fleet-border rounded-md px-3 py-2 text-sm" data-testid="sell-notes" />
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}
